import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, ChevronRight, LayoutGrid, Search, X } from "lucide-react";
import { cn } from "@weco/ui";
import { userCanAccessAdminApp } from "./adminAccess";
import type { SidebarGroup, SidebarRoute } from "./SuiteSidebar";
import type { SuiteChromeUser } from "./types";
import { GlobalSearchDropdown } from "./global-search/GlobalSearchDropdown";
import { useGlobalSearch } from "./global-search/useGlobalSearch";
import { NotificationsDropdown, type NotificationsDropdownLabels } from "./notifications/NotificationsDropdown";
import { feedDropdownProps, useOpenNotification } from "./notifications/NotificationsBell";
import { useNotifications } from "./notifications/useNotifications";

export interface MobileNavLabels {
  appsTitle?: string;
  appsEyebrow?: string;
  closeMenuLabel?: string;
  notificationsLabel?: string;
  notificationsEmptyLabel?: string;
  /** The rest of the notifications panel's strings (mark all read, states). */
  notifications?: NotificationsDropdownLabels;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  moreLabel?: string;
  navigationLabel?: string;
}

const canSee = (user: SuiteChromeUser | null | undefined) => (r: SidebarRoute) =>
  !r.requiresSuperAdmin || userCanAccessAdminApp(user ?? null);

export interface MobileNavMenuProps {
  isOpen: boolean;
  onClose: () => void;
  groups: ReadonlyArray<SidebarGroup>;
  secondary: ReadonlyArray<SidebarRoute>;
  activeRouteId: string | null;
  user?: SuiteChromeUser | null;
  brandingAppName?: string;
  brandingLogoUrl?: string;
  labels?: MobileNavLabels;
  /** Route whose tile carries the pulsing "incoming call" dot. */
  ringingRouteId?: string | null;
}

/**
 * Mobile apps sheet (design: `weco2/shell.jsx` MobileNavMenu). Opened from
 * the bottom nav's "More" or the condensed header's menu button: grouped app
 * tiles, plus the search and notifications the condensed header no longer
 * carries, plus the secondary rows (All apps, Settings).
 */
export const MobileNavMenu: React.FC<MobileNavMenuProps> = ({
  isOpen,
  onClose,
  groups,
  secondary,
  activeRouteId,
  user,
  brandingAppName = "We-Co",
  brandingLogoUrl,
  labels,
  ringingRouteId = null,
}) => {
  const {
    appsTitle = "Apps",
    appsEyebrow = "APPS",
    closeMenuLabel = "Close menu",
    notificationsLabel = "Notifications",
    notificationsEmptyLabel = "You're all caught up.",
    searchPlaceholder = "Search workspace...",
    searchAriaLabel = "Search workspace",
  } = labels ?? {};
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const { modules } = useGlobalSearch(q);
  const feed = useNotifications();
  const openNotification = useOpenNotification(feed.markRead);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const unseen = feed.unread > 0;

  useEffect(() => {
    if (!isOpen) {
      setSearchOpen(false);
      setNotifOpen(false);
      return;
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  const visible = canSee(user);

  const tile = (it: SidebarRoute) => {
    const isActive = activeRouteId === it.id;
    const Icon = it.icon;
    return (
      <Link
        key={it.id}
        to={it.path}
        onClick={onClose}
        aria-current={isActive ? "page" : undefined}
        data-tour-id={`mobile-${it.id}`}
        className="flex flex-col items-center gap-2 min-h-[80px] py-2 px-0.5 rounded-2xl hover:bg-muted transition-colors"
      >
        <span
          className={cn(
            "relative w-12 h-12 rounded-2xl flex items-center justify-center border transition-colors",
            isActive ? "bg-primary text-primary-foreground border-primary" : "bg-muted/70 border-border/60 text-foreground/80",
          )}
        >
          <Icon size={20} aria-hidden />
          {ringingRouteId === it.id && <span aria-hidden className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-background anim-pulse-rec" />}
        </span>
        <span
          data-i18n-skip="true"
          className={cn(
            "text-[11px] leading-tight text-center line-clamp-2 w-full",
            isActive ? "font-bold text-foreground" : "font-medium text-muted-foreground",
          )}
        >
          {it.label}
        </span>
      </Link>
    );
  };

  const rowClass =
    "w-full flex items-center gap-3 min-h-[48px] px-3 rounded-2xl text-[14px] font-medium text-foreground/85 hover:bg-muted transition-colors";
  const rowIcon = (Icon: React.ComponentType<{ size?: number }>, dot = false) => (
    <span className="relative shrink-0 w-9 h-9 rounded-xl bg-muted/70 flex items-center justify-center text-foreground/75">
      <Icon size={18} />
      {dot && <span className="absolute top-1.5 end-1.5 w-2 h-2 bg-primary rounded-full ring-2 ring-background" />}
    </span>
  );

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60]" aria-hidden="true" />
      <div
        id="suite-mobile-apps"
        role="dialog"
        aria-modal="true"
        aria-label={appsTitle}
        className="wc-drawer fixed inset-y-0 start-0 z-[61] w-[82%] max-w-[360px] bg-background border-e border-border rounded-e-[34px] shadow-2xl flex flex-col anim-drawer-in"
        style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="flex items-center justify-between ps-5 pe-3 pt-3 pb-1 shrink-0">
          <span className="flex items-center gap-2">
            {brandingLogoUrl ? (
              <img src={brandingLogoUrl} alt="" className="w-7 h-7 shrink-0 rounded-lg object-cover" />
            ) : (
              <span className="w-7 h-7 shrink-0 bg-primary rounded-lg flex items-center justify-center">
                <span className="font-black text-sm text-primary-foreground lowercase leading-none">w</span>
              </span>
            )}
            <span className="flex flex-col leading-none">
              <span data-i18n-skip="true" className="font-black text-sm tracking-tight text-foreground leading-none">
                {brandingAppName}
              </span>
              <span className="font-black text-[9px] tracking-[0.14em] leading-none mt-0.5" style={{ color: "var(--text-accent, var(--color-primary))" }}>
                {appsEyebrow}
              </span>
            </span>
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label={closeMenuLabel}
            title={closeMenuLabel}
            className="w-11 h-11 rounded-full flex items-center justify-center text-foreground/70 hover:bg-muted transition-colors"
          >
            <X size={20} aria-hidden />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto no-scrollbar px-4 pb-5 space-y-3 min-h-0">
          <div ref={searchRef} className="relative pt-1">
            <Search size={16} className="absolute start-3.5 top-1/2 mt-0.5 -translate-y-1/2 text-muted-foreground pointer-events-none" aria-hidden />
            <input
              type="search"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setSearchOpen(e.target.value.trim().length >= 2);
              }}
              placeholder={searchPlaceholder}
              aria-label={searchAriaLabel}
              className="h-11 w-full rounded-2xl bg-foreground/5 ps-10 pe-4 text-sm font-medium text-foreground outline-none placeholder:text-muted-foreground/70 focus:ring-2 focus:ring-primary/30"
            />
            <GlobalSearchDropdown
              open={searchOpen}
              modules={modules}
              anchorRef={searchRef}
              onSelect={(result) => {
                setSearchOpen(false);
                onClose();
                navigate(result.href);
              }}
              onClose={() => setSearchOpen(false)}
            />
          </div>
          {groups.map((g) => {
            const apps = g.routes.filter(visible);
            if (!apps.length) return null;
            return (
              <div key={g.id} className="space-y-1">
                {g.label && (
                  <h2 data-i18n-skip="true" className="text-[11px] font-black uppercase tracking-widest text-muted-foreground/60 px-1">
                    {g.label}
                  </h2>
                )}
                <div className="grid grid-cols-3 gap-1">{apps.map(tile)}</div>
              </div>
            );
          })}
          <div className="pt-3 border-t border-border/60 space-y-1">
            <div ref={notifRef} className="relative">
              <button
                type="button"
                onClick={() => setNotifOpen((c) => !c)}
                aria-expanded={notifOpen}
                aria-haspopup="menu"
                className={rowClass}
              >
                {rowIcon(Bell, unseen)}
                <span className="flex-1 text-start truncate">{notificationsLabel}</span>
                <ChevronRight size={16} className="text-muted-foreground shrink-0 rtl:rotate-180" aria-hidden />
              </button>
              <NotificationsDropdown
                open={notifOpen}
                anchorRef={notifRef}
                labels={{ empty: notificationsEmptyLabel, ...labels?.notifications, title: notificationsLabel }}
                {...feedDropdownProps(feed)}
                onSelect={(item) => {
                  setNotifOpen(false);
                  onClose();
                  openNotification(item);
                }}
                onClose={() => setNotifOpen(false)}
                compact
              />
            </div>
            {secondary.filter(visible).map((it) => (
              <Link key={it.id} to={it.path} onClick={onClose} className={rowClass}>
                {rowIcon(it.icon)}
                <span data-i18n-skip="true" className="flex-1 text-start truncate">
                  {it.label}
                </span>
                <ChevronRight size={16} className="text-muted-foreground shrink-0 rtl:rotate-180" aria-hidden />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export interface BottomNavItem extends SidebarRoute {
  /** Shorter label for the bar (e.g. "Hub"). */
  shortLabel?: string;
}

export interface BottomNavProps {
  items: ReadonlyArray<BottomNavItem>;
  activeRouteId: string | null;
  moreOpen: boolean;
  onMore: () => void;
  labels?: MobileNavLabels;
  /** Route that is ringing; its dot lands on "More" when the bar doesn't list it. */
  ringingRouteId?: string | null;
}

/**
 * Mobile bottom navigation (design: BottomNav): four most-used apps + More.
 * The shell hides it inside focused editors and Call, which own the bottom
 * edge with their own toolbars.
 */
export const BottomNav: React.FC<BottomNavProps> = ({ items, activeRouteId, moreOpen, onMore, labels, ringingRouteId = null }) => {
  const { moreLabel = "More", navigationLabel = "Primary navigation" } = labels ?? {};
  const inPrimary = items.some((b) => b.id === activeRouteId);
  const ringingInBar = ringingRouteId !== null && items.some((b) => b.id === ringingRouteId);
  const pill = (active: boolean, Icon: React.ComponentType<{ size?: number; strokeWidth?: number }>, dot = false) => (
    <span
      className={cn(
        "relative h-8 w-14 rounded-full flex items-center justify-center transition-colors",
        active ? "bg-primary/25 text-foreground" : "text-foreground/60",
      )}
    >
      <Icon size={20} strokeWidth={active ? 2.25 : 2} />
      {dot && <span aria-hidden className="absolute top-0.5 right-3 w-2.5 h-2.5 bg-emerald-500 rounded-full ring-2 ring-background anim-pulse-rec" />}
    </span>
  );
  const text = (active: boolean, label: string) => (
    <span
      data-i18n-skip="true"
      className={cn(
        "text-[11px] leading-none truncate max-w-full",
        active ? "font-bold text-foreground" : "font-medium text-muted-foreground",
      )}
    >
      {label}
    </span>
  );
  const moreActive = moreOpen || !inPrimary;
  return (
    <nav
      aria-label={navigationLabel}
      className="wc-bottom-nav shrink-0 relative z-40 bg-background border-t border-border px-2"
      style={{
        height: "calc(var(--bnav-h, 64px) + env(safe-area-inset-bottom))",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div className="h-full w-full max-w-[560px] mx-auto flex items-center gap-1">
        {items.map((b) => {
          const active = activeRouteId === b.id && !moreOpen;
          return (
            <Link
              key={b.id}
              to={b.path}
              aria-current={active ? "page" : undefined}
              className="flex-1 min-w-0 min-h-[48px] flex flex-col items-center justify-center gap-1 rounded-2xl"
            >
              {pill(active, b.icon, ringingRouteId === b.id)}
              {text(active, b.shortLabel ?? b.label)}
            </Link>
          );
        })}
        <button
          type="button"
          onClick={onMore}
          aria-expanded={moreOpen}
          aria-controls="suite-mobile-apps"
          className="flex-1 min-w-0 min-h-[48px] flex flex-col items-center justify-center gap-1 rounded-2xl"
        >
          {pill(moreActive, LayoutGrid, ringingRouteId !== null && !ringingInBar)}
          {text(moreActive, moreLabel)}
        </button>
      </div>
    </nav>
  );
};
